import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3 } from '@/components/Element/Heading';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import StateHeaderSection from '@/components/StateHeaderSection/StateHeaderSection';

export default function Page() {
  return (
    <article>
      <MetaTags
        title="Prince Edward Island's Home Solar Benefits: Incentives, Credits & Policies"
        description="Find available incentives, tax credits, and utility policies for home solar in Prince Edward Island with EnergyPal. Switch to solar energy today."
      />
      {/* <SimpleCenteredHeaderSection
        dark
        supTitle="Prince Edward Island Solar"
        title="Home Solar Battery Installers Near Prince Edward Island"
        // January 18, 2022
        subTitle=" Let’s learn about some of those incentives, the utility policies associated with interconnection and setup, and special solar financing plans for solar panels and systems in Prince Edward Island."
      /> */}
      <StateHeaderSection dark state="Prince Edward Island" />

      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar & Battery Installers Near Me',
            href: '/home-battery-solar-installers-near-me',
          },
          {
            name: 'Prince Edward Island Solar',
            href: '/home-battery-solar-installers-near-me/pei-prince-edward-island',
          },
        ]}
      />
      <CenteredContentSection>
        <p>
          Prince Edward Island(PEI) is a small province in eastern Canada (both
          small in size and population). However, it is the most densely
          populated Canadian province, with the capital city of Charlottetown
          having the most people.
        </p>
        <p>
          Prince Edward Island’s provincial government policies favor solar
          rebates and incentives. In addition to rebates that help with upfront
          solar system costs, higher energy prices make it a favorable place for
          businesses and residents to invest in solar energy.
        </p>
        <H3>Solar Incentives</H3>
        <p>
          Prince Edward Island provides multiple solar energy rebates and
          incentives that help to reduce the overall cost of solar systems.
        </p>
        <p>
          Our EnergyPal Advisors can help you navigate all of the solar
          incentives available in Prince Edward Island and also apply them to
          the available solar panel offerings.
        </p>
        <p>
          <em>Just remember these items:</em>
        </p>
        <ul>
          <li>
            <strong>Per Watt Rebates</strong>
            <p>
              Rebates in Prince Edward Island are given according to the size of
              your installed system, which is measured in watts. Watts is the
              measure of the potential output of your system, usually described
              in kilowatts.
            </p>
          </li>
          <li>
            <strong>Eligible Costs</strong>
            <p>
              Rebate maximums are based on a percentage of your total eligible
              spend. The costs of materials and installation count towards
              eligible costs. Any taxes you may have paid do not count.
            </p>
          </li>
          <li>
            <strong>Installation Qualifications</strong>
            <p>
              Your installation must meet certain qualifications to be eligible
              for rebates. One of these qualifications is that an Efficiency
              Prince Edward Island qualified installer must install your system
              using CSA-approved electric equipment. Don’t worry—our partners
              are all qualified installers.
            </p>
          </li>
          <li>
            <strong>Simple Rebate Applications</strong>
            <p>
              We’ve screened our installation companies to make sure that they
              can and will handle all paperwork pertaining to rebates. We will
              make sure that you recieve the best rates possible.
            </p>
          </li>
        </ul>

        <H3>Rebates and Tax Breaks</H3>
        <p>
          The{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.princeedwardisland.ca/en/information/transportation-infrastructure-and-energy/solar-electric-rebate-program"
          >
            Solar Electric Rebate Program
          </a>
          , which is administered by efficiencyPEI, is the principal solar
          incentive in the province of Prince Edward Island. Residential
          property owners who belong to this incentive program can earn a cash
          rebate of $1.00 per watt for solar power installations. The maximum
          rebate amount is 40% of incurred costs up to $10,000.
        </p>
        <p>If you had a 9.28 kW system, your savings would look like this:</p>
        <p>
          <em>9280 watts x $1.00/watt = </em>
          <strong>$9,280</strong>
        </p>
        <p>
          So if you spent $20,000 on that system, your cost would go down to
          $10,720 after the rebate.
        </p>
        <H3>Other Energy Incentives</H3>
        <p>
          Find out about other residential rebate programs in Prince Edward
          Island by exploring the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.princeedwardisland.ca/en/topic/energy-efficiency"
          >
            efficiencyPEI website
          </a>
          . You can also look into additional incentives for low-income
          residents through the{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.princeedwardisland.ca/en/information/transportation-infrastructure-and-energy/winter-warming"
          >
            Home Energy Low-Income Program
          </a>
          .
        </p>
        <p>
          The{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.princeedwardisland.ca/en/information/transportation-infrastructure-and-energy/solar-electric-rebate-program"
          >
            Solar Electric Rebate Program
          </a>{' '}
          also helps businesses and farms pay for their solar systems. Farms and
          commercial property owners who belong to this incentive program can
          earn a rebate of $3.50 per watt for solar power installations. The
          maximum rebate amount is 40% of incurred costs up to $10,000.
        </p>
        <H3>Utility Policies</H3>
        <p>
          Utility policy factors determine what your utility provider pays you
          for the excess power you produce and what you save on your power bill
          by reducing electricity usage. Prince Edward Island gets a medium
          score for utility policies.
        </p>
        <H3>Interconnection Policy</H3>
        <p>
          One important policy for solar customers is called Net Metering. Net
          Metering is a billing mechanism that allows solar customers to push
          electricity to the grid for credit that can be used later, like at
          night when your solar panels aren’t generating power. This
          incentivizes people to go solar, provides clean energy for others to
          use and prevents waste.
        </p>
        <p>
          Some provinces have poorer net metering policies than others, meaning
          that credits may not be able to be rolled over to the next month or
          year, or that you don’t earn full watt-for-watt credit for your excess
          energy.
        </p>
        <p>
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.maritimeelectric.com/services/services/net-metering/"
          >
            Prince Edward Island’s Net Metering Program
          </a>{' '}
          is good. Credits can be carried over into the next month, and you can
          connect a system up to 100 kW in size. Unfortunately, at the end of
          the year, you are not compensated for excess credits.
        </p>
        <H3>Solar Setup Fees</H3>
        <p>
          Prince Edward Island’s program does not charge for installation of a
          bi-directional meter, which is required for net metering. Other
          provinces do charge this fee.
        </p>
        <H3>Electricity Prices</H3>
        <p>
          Electricity prices in Prince Edward Island are on the high end.
          Usually you don’t want high prices, but with solar, the higher the
          prices, the more you save, and the more economical a solar system
          becomes.
        </p>
        <p>
          The average total cost of electricity in Prince Edward Island is
          $0.168 per kWh, including fixed and variable costs, and assuming a
          monthly usage of 1,000 kWh. Under similar assumptions, the Canadian
          national average for all provinces is $0.135 per kWh.
        </p>
        <H3>Rate Design</H3>
        <p>
          Rate designs are comprised of monthly fees and either a flat or tiered
          rate you pay for electricity. Good rate designs, which save you money
          when you conserve energy, are tiered and have low fixed monthly fees.
          Poor rate designs have flat rates and high fixed monthly fees.
        </p>
        <p>
          Prince Edward Island scores poorly when it comes to the quality of its
          electricity bill rate design. It has reverse tiered rates, meaning you
          pay less per watt if you use more energy, and high fixed fees (around
          $25 per month).
        </p>
        <p>
          You still pay fixed monthly fees when you go solar because you are
          still tied to the grid. Being tied to the grid is a good thing,
          because otherwise you might not have power at night or if your system
          stopped working suddenly. Even if you go off of the grid and rely on a
          battery source, the high cost of the battery would not outweigh the
          monthly fee and the overall savings obtained from a net metering
          program.
        </p>
        <H3>PACE Programs</H3>
        <p>
          PACE stands for Property Assessed Clean Energy and is a financing
          program for solar projects that is tied to the home instead of the
          homeowner. The PACE program is available in many communities in
          Canada, but not in Prince Edward Island.
        </p>
        <p>
          For zero-down, you can finance your solar system, but the
          low-interest, long-term loan is not attached to you; it’s attached to
          your home. It is paid back as a Local Improvement Charge (LIC) on your
          property tax bill. To qualify, you must own a certain portion of your
          home.
        </p>
        <H3>Other Energy Financing</H3>
        <p>
          Solar systems can be financed by bank loans, solar installer
          financing, new home mortgages, energy loans and home equity loans or
          lines of credit. Cash works too. Prince Edward Island has a special
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.princeedwardisland.ca/en/information/finance-pei/energy-efficiency-loan-program"
          >
            Energy Efficiency Loan Program
          </a>{' '}
          to assist with initial costs of a solar system or solar hot water
          heater. This program offers repayable, low-interest loans of up to
          $10,000 over a period of up to seven years. Check the website for
          additional rebates, stipulations and terms.
        </p>
        <p>
          Talk to your EnergyPal Advisor about financing solar options in Prince
          Edward Island. We regularly source the best deals and terms for
          customers.
        </p>
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
